import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"
import { CoachLayout } from "./components/CoachLayout"
import { Button } from "@/components/ui/button"
import { formatPeriodKey, formatMonthKey } from "@/lib/period-format"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { LifeWheelRadar } from "@/components/planning/LifeWheelRadar"
import { ProspectAnswersList } from "@/components/planning/ProspectAnswersList"
import { RockGrid } from "@/components/planning/RockCard"
import { KpiScorecard } from "@/components/planning/KpiScorecard"
import { CourseHistoryCard } from "./components/CourseHistoryCard"
import { CertificationsCard, QuizProgressCard } from "@/components/certification/CertificationCards"
import { LessonAnswersCard } from "./components/LessonAnswersCard"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  ArrowLeft,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  FileDown,
  LineChart,
  Trash2,
} from "lucide-react"

function MagicLinkCopyBlock({ inviteLink }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink)
    } catch {
      const ta = document.createElement("textarea")
      ta.value = inviteLink
      document.body.appendChild(ta)
      ta.select()
      document.execCommand("copy")
      document.body.removeChild(ta)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-zinc-500">
        Magic link — reusable until it expires
      </Label>
      <div className="mt-1 flex gap-2">
        <Input
          value={inviteLink}
          readOnly
          onFocus={(e) => e.target.select()}
          className="font-mono text-xs"
        />
        <Button type="button" variant={copied ? "secondary" : "outline"} size="sm" onClick={copy}>
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 mr-1" /> Copied
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
            </>
          )}
        </Button>
      </div>
      <p className="text-xs text-zinc-500 mt-1">
        Paste into a personal email so it reads as coming from you, not an automated blast.
      </p>
    </div>
  )
}

function summarizeKpiResults(results) {
  if (!Array.isArray(results) || results.length === 0) return null
  const counts = { plus: 0, minus: 0, equal: 0 }
  results.forEach((r) => {
    const s = r?.status
    if (s === "plus" || s === "minus" || s === "equal") counts[s] += 1
  })
  return counts
}

function CountsLine({ counts, kind }) {
  if (!counts) return null
  const total = counts.plus + counts.minus + counts.equal
  if (total === 0) return null
  return (
    <span className="text-xs text-zinc-500">
      {kind}: {counts.plus} +, {counts.minus} −, {counts.equal} =
    </span>
  )
}

function QuarterHistoryCard({ q }) {
  const wheelTotals = q.wheelTotals || {}
  const hasWheelData = Object.values(wheelTotals).some((v) => v && v > 0)
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {formatPeriodKey(q.quarterKey)}
          {q.quarterStartAt && q.quarterEndAt && (
            <span className="text-xs text-zinc-500 font-normal ml-2">
              {new Date(q.quarterStartAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              {" – "}
              {new Date(q.quarterEndAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasWheelData && (
          <div className="flex justify-center">
            <LifeWheelRadar scores={wheelTotals} size={200} />
          </div>
        )}
        {q.rocks && q.rocks.length > 0 ? (
          <RockGrid
            rocks={q.rocks}
            quarterStartAt={q.quarterStartAt}
            quarterEndAt={q.quarterEndAt}
            readOnly={true}
            showFinalized={true}
          />
        ) : (
          <p className="text-sm text-zinc-500 italic">No rocks recorded for this quarter.</p>
        )}
      </CardContent>
    </Card>
  )
}

function MonthHistoryCard({ m }) {
  const [expanded, setExpanded] = useState(false)
  const kpiCounts = summarizeKpiResults(m.kpiResults)
  const koiCounts = summarizeKpiResults(m.koiResults)
  const hasNotes = m.rose || m.bud || m.thorn || m.thirtyDayImperative
  const Caret = expanded ? ChevronDown : ChevronRight

  return (
    <div className="rounded-lg border border-zinc-200 bg-white">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-50"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-3">
          <Caret className="w-4 h-4 text-zinc-500 shrink-0" />
          <span className="font-medium text-zinc-900">{formatMonthKey(m.monthKey)}</span>
        </div>
        <div className="flex items-center gap-3">
          <CountsLine counts={kpiCounts} kind="KPIs" />
          <CountsLine counts={koiCounts} kind="KOIs" />
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-2 text-sm border-t border-zinc-100">
          {!hasNotes && (
            <p className="text-zinc-500 italic">No reflection notes for this month.</p>
          )}
          {m.thirtyDayImperative && (
            <p>
              <strong className="text-zinc-700">30-day imperative:</strong>{" "}
              <span className="text-zinc-900">{m.thirtyDayImperative}</span>
            </p>
          )}
          {m.rose && (
            <p>
              <strong className="text-zinc-700">Rose:</strong>{" "}
              <span className="text-zinc-900">{m.rose}</span>
            </p>
          )}
          {m.bud && (
            <p>
              <strong className="text-zinc-700">Bud:</strong>{" "}
              <span className="text-zinc-900">{m.bud}</span>
            </p>
          )}
          {m.thorn && (
            <p>
              <strong className="text-zinc-700">Thorn:</strong>{" "}
              <span className="text-zinc-900">{m.thorn}</span>
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function AnnualPlanHistoryCard({ plan }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">One-year plan ({plan.year})</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-zinc-500 text-xs uppercase tracking-wide">Revenue target</p>
          <p className="text-base font-medium">
            {plan.revenueTarget ? `$${Number(plan.revenueTarget).toLocaleString()}` : "—"}
          </p>
        </div>
        <div>
          <p className="text-zinc-500 text-xs uppercase tracking-wide">Profit target</p>
          <p className="text-base font-medium">
            {plan.profitTarget ? `$${Number(plan.profitTarget).toLocaleString()}` : "—"}
          </p>
        </div>
        {Array.isArray(plan.annualKpis) && plan.annualKpis.length > 0 && (
          <div className="sm:col-span-2">
            <p className="text-zinc-500 text-xs uppercase tracking-wide">Annual KPIs</p>
            <ul className="list-disc ml-5 text-zinc-900">
              {plan.annualKpis.map((kpi, idx) => (
                <li key={kpi.id || idx}>
                  {kpi.name} {kpi.target ? `— ${kpi.target}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
        {Array.isArray(plan.annualKois) && plan.annualKois.length > 0 && (
          <div className="sm:col-span-2">
            <p className="text-zinc-500 text-xs uppercase tracking-wide">Annual KOIs</p>
            <ul className="list-disc ml-5 text-zinc-900">
              {plan.annualKois.map((koi, idx) => (
                <li key={koi.id || idx}>
                  {koi.name} {koi.target ? `— ${koi.target}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
        {Array.isArray(plan.issues) && plan.issues.length > 0 && (
          <div className="sm:col-span-2">
            <p className="text-zinc-500 text-xs uppercase tracking-wide">Issues</p>
            <ul className="list-disc ml-5 text-zinc-900">
              {plan.issues.map((issue, idx) => (
                <li key={issue.id || idx}>{issue.text}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// R17: the prospect's 10 peer-group answers, surfaced to the coach as
// sales-conversation context. Carries forward after conversion (the report
// is never destroyed on promotion).
function ProspectAnswersCard({ answers }) {
  const hasAny = (answers?.open_ended?.length || 0) + (answers?.yes_no?.length || 0) > 0
  if (!hasAny) return null

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg">Their assessment answers</CardTitle>
        <p className="text-sm text-zinc-600 font-normal">
          What they shared during the peer-group assessment — context for your
          next conversation.
        </p>
      </CardHeader>
      <CardContent>
        <ProspectAnswersList answers={answers} />
      </CardContent>
    </Card>
  )
}

export default function MemberShow({
  currentUser,
  member,
  annualPlan,
  currentQuarter,
  currentMonth,
  scorecard,
  latestWheelSnapshot = null,
  businessPlanPdfUrl = null,
  prospectAnswers = null,
  inviteLink,
  quarterHistory = [],
  monthHistory = [],
  annualPlanHistory = [],
  backLink,
  courseHistory = null,
  lessonAnswers = null,
  certifications = [],
  quizProgress = [],
  quizAnswers = null,
}) {
  const [deleteOpen, setDeleteOpen] = useState(false)
  const back = backLink || { href: "/app/coach/users", label: "Back to Users", key: "users" }
  const backKey = back.key || "users"

  const confirmDeleteProspect = () => {
    router.delete(`/app/coach/members/${member.id}?back=${encodeURIComponent(backKey)}`)
  }

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`${member.name} — read-only view`} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link
          href={back.href}
          className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {back.label}
        </Link>

        <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
          <h1 className="font-display text-3xl">{member.name}</h1>
          <div className="flex items-center gap-3 flex-wrap">
            {member.role === "guest" && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Delete
              </Button>
            )}
            {businessPlanPdfUrl && (
              <a
                href={businessPlanPdfUrl}
                className="text-sm text-kf-red hover:underline inline-flex items-center gap-1"
                title="Download this member's Business Plan + Life Audit as a PDF"
              >
                <FileDown className="w-3.5 h-3.5" /> Business Plan PDF
              </a>
            )}
          </div>
        </div>
        <p className="text-zinc-500 mb-6 capitalize">
          {member.role} · {member.email}
        </p>

        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-900 mb-6">
          Read-only. Members own their plan; you observe and coach in person.
        </div>

        <ProspectAnswersCard answers={prospectAnswers} />

        {member.role === "guest" && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Assessment invite</CardTitle>
              <p className="text-sm text-zinc-600 font-normal">
                Copy their sign-in link to resend personally, or nudge them with a reminder email once
                per day.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {inviteLink ? (
                <MagicLinkCopyBlock inviteLink={inviteLink} />
              ) : (
                <p className="text-sm text-zinc-600">
                  No active magic link found (it may have expired). Invite them again from the Prospects
                  list.
                </p>
              )}

            </CardContent>
          </Card>
        )}

        {currentMonth && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>This month: {formatMonthKey(currentMonth.monthKey)}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {currentMonth.thirtyDayImperative && (
                <p>
                  <strong>30-day imperative:</strong>{" "}
                  {currentMonth.thirtyDayImperative}
                </p>
              )}
              {currentMonth.rose && (
                <p>
                  <strong>Rose:</strong> {currentMonth.rose}
                </p>
              )}
              {currentMonth.bud && (
                <p>
                  <strong>Bud:</strong> {currentMonth.bud}
                </p>
              )}
              {currentMonth.thorn && (
                <p>
                  <strong>Thorn:</strong> {currentMonth.thorn}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {(latestWheelSnapshot || currentQuarter) && (
          <section className="mb-6 space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>
                  Integrated Life Audit
                  {latestWheelSnapshot?.takenAt && (
                    <span className="text-sm text-zinc-500 font-normal ml-2">
                      — taken {new Date(latestWheelSnapshot.takenAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  )}
                  {!latestWheelSnapshot && currentQuarter && (
                    <span className="text-sm text-zinc-500 font-normal ml-2">
                      — {formatPeriodKey(currentQuarter.quarterKey)}
                    </span>
                  )}
                </CardTitle>
                <Link
                  href={`/app/coach/members/${member.id}/wheel_history`}
                  className="text-sm text-kf-red hover:underline inline-flex items-center gap-1"
                >
                  <LineChart className="w-4 h-4" />
                  Wheel history
                </Link>
              </CardHeader>
              <CardContent>
                <LifeWheelRadar
                  scores={
                    latestWheelSnapshot?.wheelTotals ||
                    currentQuarter?.wheelTotals ||
                    {}
                  }
                  size={280}
                />
              </CardContent>
            </Card>

            {currentQuarter && (
              <div>
                <h3 className="font-display text-lg text-zinc-900 mb-3">
                  Current rocks
                </h3>
                <RockGrid
                  rocks={currentQuarter.rocks || []}
                  quarterStartAt={currentQuarter.quarterStartAt}
                  quarterEndAt={currentQuarter.quarterEndAt}
                  readOnly={true}
                />
              </div>
            )}
          </section>
        )}

        {scorecard && (
          <section className="mb-6 space-y-6">
            <KpiScorecard
              items={scorecard.kpis}
              heading="Annual KPIs"
              emptyMessage="This member hasn't defined KPIs yet."
            />
            <KpiScorecard
              items={scorecard.kois}
              heading="Annual KOIs"
              emptyMessage="This member hasn't defined KOIs yet."
            />
          </section>
        )}

        {annualPlan && (
          <Card>
            <CardHeader>
              <CardTitle>One-year plan ({annualPlan.year})</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-zinc-500">Revenue target</p>
                <p className="text-lg font-medium">
                  {annualPlan.revenueTarget
                    ? `$${Number(annualPlan.revenueTarget).toLocaleString()}`
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-zinc-500">Profit target</p>
                <p className="text-lg font-medium">
                  {annualPlan.profitTarget
                    ? `$${Number(annualPlan.profitTarget).toLocaleString()}`
                    : "—"}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <CertificationsCard certifications={certifications} />
        <QuizProgressCard quizProgress={quizProgress} accountId={member.id} quizAnswers={quizAnswers} />

        {courseHistory && <CourseHistoryCard history={courseHistory} memberName={member.name} />}

        {lessonAnswers && <LessonAnswersCard answers={lessonAnswers} memberName={member.name} />}

        <section className="mt-8 space-y-4">
          <h2 className="font-display text-2xl text-zinc-900">Quarter history</h2>
          {quarterHistory.length === 0 ? (
            <p className="text-sm text-zinc-500 italic">No previous quarters yet.</p>
          ) : (
            <div className="space-y-4">
              {quarterHistory.map((q) => (
                <QuarterHistoryCard key={q.quarterKey} q={q} />
              ))}
            </div>
          )}
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="font-display text-2xl text-zinc-900">Month history</h2>
          {monthHistory.length === 0 ? (
            <p className="text-sm text-zinc-500 italic">No previous months yet.</p>
          ) : (
            <div className="space-y-2">
              {monthHistory.map((m) => (
                <MonthHistoryCard key={m.monthKey} m={m} />
              ))}
            </div>
          )}
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="font-display text-2xl text-zinc-900">Annual plan history</h2>
          {annualPlanHistory.length === 0 ? (
            <p className="text-sm text-zinc-500 italic">No annual plans yet.</p>
          ) : (
            <div className="space-y-4">
              {annualPlanHistory.map((p) => (
                <AnnualPlanHistoryCard key={p.year} plan={p} />
              ))}
            </div>
          )}
        </section>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this prospect?</AlertDialogTitle>
            <AlertDialogDescription>
              This deactivates {member.name}&rsquo;s account. They will not be able to sign in until an admin
              reactivates them or you invite them again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteProspect}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove prospect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </CoachLayout>
  )
}
